import { rest } from '@0xtorch/core'
import { z } from 'zod'
import type { AssetBalance, GetCurrentCexAssetsParameters } from '../../types'
import { bitbankApiEndpoint } from '../constants'
import { errorResponseSchema } from '../schemas'
import { createPrivateGetHeaders, getAsset } from '../utils'
import { assetSchema } from './schema'

const urlPath = '/v1/user/assets'

const responseSchema = z.object({
  success: z.literal(1),
  data: z.object({
    assets: z.array(assetSchema),
  }),
})

export const getCurrentBitbankAssets = async ({
  apiKey,
  apiSecret,
  proxy,
  proxyHeaders,
  cryptoCurrencies,
  fiatCurrencies,
}: GetCurrentCexAssetsParameters): Promise<readonly AssetBalance[]> => {
  const timestamp = Date.now()
  const url = new URL(urlPath, bitbankApiEndpoint)
  const response = await rest(
    proxy === undefined
      ? url.toString()
      : `${proxy}${encodeURIComponent(url.toString())}`,
    {
      fetchOptions: {
        method: 'GET',
        headers: {
          ...proxyHeaders,
          ...(await createPrivateGetHeaders({
            apiKey,
            apiSecret,
            urlPath: url.pathname,
            query: '',
          })),
        },
      },
      schema: z.union([responseSchema, errorResponseSchema]),
    },
  )
  if (response.success === 0) {
    throw new Error(`Error: ${response.data.code}`)
  }

  const assets: AssetBalance[] = []
  for (const assetData of response.data.assets) {
    if (assetData.onhand_amount.value <= 0n) {
      continue
    }
    const asset = getAsset({
      asset: assetData.asset,
      cryptoCurrencies,
      fiatCurrencies,
    })
    if (asset === undefined) {
      continue
    }
    assets.push({
      key: 'bitbank_onhand',
      cryptoId: asset.type === 'CryptoCurrency' ? asset.id : undefined,
      fiatId: asset.type === 'FiatCurrency' ? asset.id : undefined,
      amount: assetData.onhand_amount,
      timestamp,
    })
  }

  return assets
}
